import { useCallback, useEffect, useRef, useState } from "react";

// Manages the Kokoro neural TTS running inside a Web Worker.
// status: "idle" | "loading" | "ready" | "error"
export function useKokoroTTS() {
  const workerRef = useRef(null);
  const genRef = useRef(null);
  const statusRef = useRef("idle");

  const [status, setStatusState] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [device, setDevice] = useState(null);

  const setStatus = (s) => {
    statusRef.current = s;
    setStatusState(s);
  };

  const ensureWorker = useCallback(() => {
    if (workerRef.current) return workerRef.current;
    const w = new Worker(new URL("../workers/kokoroWorker.js", import.meta.url), { type: "module" });
    w.onmessage = (e) => {
      const d = e.data;
      if (d.type === "progress") {
        setProgress(d.progress);
      } else if (d.type === "ready") {
        setStatus("ready");
        setProgress(100);
        setDevice(d.device);
      } else if (d.type === "audio") {
        setGenerating(false);
        const blob = new Blob([d.wav], { type: "audio/wav" });
        if (genRef.current) {
          genRef.current.resolve(blob);
          genRef.current = null;
        }
      } else if (d.type === "error") {
        setError(d.error);
        setGenerating(false);
        if (statusRef.current !== "ready") setStatus("error");
        if (genRef.current) {
          genRef.current.reject(new Error(d.error));
          genRef.current = null;
        }
      }
      // "info" messages are advisory only (e.g. webgpu fallback).
    };
    w.onerror = (ev) => {
      setError(ev.message || "Worker crashed");
      if (statusRef.current !== "ready") setStatus("error");
      setGenerating(false);
      if (genRef.current) {
        genRef.current.reject(new Error(ev.message || "Worker crashed"));
        genRef.current = null;
      }
    };
    workerRef.current = w;
    return w;
  }, []);

  useEffect(() => {
    return () => {
      if (workerRef.current) workerRef.current.terminate();
    };
  }, []);

  const load = useCallback(async () => {
    const w = ensureWorker();
    setStatus("loading");
    setProgress(0);
    setError(null);
    // Only use WebGPU if an adapter is actually obtainable.
    let dev = "wasm";
    try {
      if (typeof navigator !== "undefined" && navigator.gpu) {
        const adapter = await navigator.gpu.requestAdapter();
        if (adapter) dev = "webgpu";
      }
    } catch {
      dev = "wasm";
    }
    w.postMessage({ type: "load", device: dev });
  }, [ensureWorker]);

  // Returns a Promise<Blob> of the generated WAV audio.
  const generate = useCallback((text, voice, speed = 1) => {
    return new Promise((resolve, reject) => {
      if (!workerRef.current || statusRef.current !== "ready") {
        reject(new Error("Model not ready"));
        return;
      }
      if (genRef.current) {
        reject(new Error("A generation is already in progress"));
        return;
      }
      setGenerating(true);
      setError(null);
      genRef.current = { resolve, reject };
      workerRef.current.postMessage({ type: "generate", text, voice, speed });
    });
  }, []);

  return { status, progress, generating, error, device, load, generate };
}
